import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Marker, Polyline, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';

import { LeafletMap } from './LeafletMap';
import { MapsetType } from '../../config/mapyApi';
import { DEFAUL_MARKER_ICON, MARKER_PLACE_ICON_KDETOSAKRA } from '../../constants/icons';
import { mapLayers } from '../../redux/reducers/game';

// Custom ikony - rozměry odpovídají skutečným rozměrům obrázků
const guessMarkerIcon = L.icon({
    iconUrl: DEFAUL_MARKER_ICON,
    iconSize: [22, 31],  // marker.png je 22x31
    iconAnchor: [11, 31],
});

const panoramaMarkerIcon = L.icon({
    iconUrl: MARKER_PLACE_ICON_KDETOSAKRA,
    iconSize: [31, 31],  // guessedPlace.png je 31x31 (čtvercový)
    iconAnchor: [15, 31],
});

interface GuessedPoint {
    pointMap: { x: number; y: number };
    pointPanorama: { x: number; y: number };
}

interface GameMapProps {
    /** Callback při kliknutí na mapu - vrací souřadnice [lat, lon] */
    onMapClick?: (coords: { lat: number; lon: number }) => void;
    /** Počáteční střed mapy [lat, lon] */
    center?: [number, number];
    /** Počáteční zoom */
    zoom?: number;
    /** CSS třída */
    className?: string;
    /** Aktuální mód mapy (pro redux) */
    currentMapLayer?: string;
    /** Callback při změně vrstvy */
    onLayerChange?: (layer: string) => void;
    /** Zobrazit výsledek aktuálního kola (pro battle mód) */
    currentRoundGuessedPoint?: GuessedPoint | null;
    /** Callback pro získání reference na mapu */
    onMapReady?: (map: L.Map) => void;
}

/**
 * Komponenta pro zachycení kliknutí na mapu
 */
const MapClickHandler: React.FC<{
    onClick?: (coords: { lat: number; lon: number }) => void;
    guessPosition: [number, number] | null;
    setGuessPosition: (pos: [number, number] | null) => void;
}> = ({ onClick, guessPosition, setGuessPosition }) => {
    useMapEvents({
        click: (e) => {
            const { lat, lng } = e.latlng;
            setGuessPosition([lat, lng]);
            onClick?.({ lat, lon: lng });
        },
    });

    return guessPosition ? (
        <Marker position={guessPosition} icon={guessMarkerIcon} />
    ) : null;
};

/**
 * Komponenta pro přesun středu mapy při změně kola
 * Používá jednotlivé hodnoty lat/lon místo pole pro stabilní dependencies
 */
const MapCenterUpdater: React.FC<{
    center: [number, number];
    zoom: number;
    initialRender: React.MutableRefObject<boolean>;
}> = ({ center, zoom, initialRender }) => {
    const map = useMap();
    const [lat, lon] = center;

    useEffect(() => {
        // Přeskočíme první render
        if (initialRender.current) {
            initialRender.current = false;
            return;
        }

        // Resetujeme pohled na nový střed s výchozím zoomem
        map.setView([lat, lon], zoom, { animate: true });
    }, [lat, lon, zoom, map]);

    return null;
};

/**
 * Herní mapa pro hádání místa
 *
 * Podporuje:
 * - Kliknutí pro umístění markeru
 * - Přepínání vrstev (basic/outdoor)
 * - Reset při změně kola
 * - Zobrazení výsledku kola (pro battle mód)
 *
 * @example
 * <GameMap
 *     center={[50.0, 15.0]}
 *     onMapClick={(coords) => console.log(coords)}
 * />
 */
export const GameMap: React.FC<GameMapProps> = ({
    onMapClick,
    center = [50.027429, 15.202828],
    zoom = 7,
    className = '',
    currentMapLayer,
    onLayerChange,
    currentRoundGuessedPoint,
    onMapReady,
}) => {
    const [guessPosition, setGuessPosition] = useState<[number, number] | null>(null);
    const initialRender = useRef(true);
    const prevCenter = useRef(center);

    // Reset markeru při změně středu (nové kolo)
    useEffect(() => {
        if (
            prevCenter.current[0] !== center[0] ||
            prevCenter.current[1] !== center[1]
        ) {
            setGuessPosition(null);
            prevCenter.current = center;
        }
    }, [center]);

    const handleLayerChange = useCallback((mapset: MapsetType) => {
        if (onLayerChange) {
            const layer = mapset === 'outdoor' ? mapLayers.tourist : mapLayers.default;
            onLayerChange(layer);
        }
    }, [onLayerChange]);

    const initialMapset = currentMapLayer === mapLayers.tourist ? 'outdoor' : 'basic';

    return (
        <div id="smap" className={`smap smap-style ${className}`} style={{ position: 'relative' }}>
            <LeafletMap
                center={center}
                zoom={zoom}
                initialMapset={initialMapset}
                showLayerControl
                onLayerChange={handleLayerChange}
                whenReady={onMapReady}
            >
                <MapCenterUpdater center={center} zoom={zoom} initialRender={initialRender} />
                <MapClickHandler
                    onClick={onMapClick}
                    guessPosition={guessPosition}
                    setGuessPosition={setGuessPosition}
                />

                {/* Zobrazení výsledku kola (pro battle mód když je kolo ukončeno) */}
                {currentRoundGuessedPoint && (
                    <>
                        <Marker
                            position={[
                                currentRoundGuessedPoint.pointMap.y,
                                currentRoundGuessedPoint.pointMap.x,
                            ]}
                            icon={guessMarkerIcon}
                        />
                        <Marker
                            position={[
                                currentRoundGuessedPoint.pointPanorama.y,
                                currentRoundGuessedPoint.pointPanorama.x,
                            ]}
                            icon={panoramaMarkerIcon}
                        />
                        <Polyline
                            positions={[
                                [
                                    currentRoundGuessedPoint.pointMap.y,
                                    currentRoundGuessedPoint.pointMap.x,
                                ],
                                [
                                    currentRoundGuessedPoint.pointPanorama.y,
                                    currentRoundGuessedPoint.pointPanorama.x,
                                ],
                            ]}
                            color="#f00"
                            weight={3}
                        />
                    </>
                )}
            </LeafletMap>
            {/* Logo Mapy.com - vlevo dole */}
            <a
                href="https://mapy.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                    position: 'absolute',
                    bottom: '4px',
                    left: '4px',
                    zIndex: 1000,
                    background: 'rgba(255, 255, 255, 0.9)',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    display: 'block',
                }}
            >
                <img
                    src="https://api.mapy.com/img/api/logo.svg"
                    alt="Mapy.com"
                    style={{ height: '28px', display: 'block' }}
                />
            </a>
        </div>
    );
};

export default GameMap;
